/**
 * Aarrearkku catalogue (relocated to `@sagamoon/shared` in Story 19.11).
 *
 * The 30-item treasure catalogue plus its rarity ordering and item shape.
 * Both the frontend (`useTreasureCollection`, `ItemTile`, etc.) and the
 * backend (`aarrearkkuOnStoryCreated` Firestore trigger, `generate-flavor`
 * helper) read from the same source so the matcher → drop selector → flavor
 * pipeline runs identically on either side of the wire.
 *
 * The frontend's `apps/web/src/components/collection/collection-mock-data.ts`
 * re-exports the catalogue plus a `TranslationKey`-narrowed `TreasureItem<KeyT>`
 * alias so the React UI keeps full type-narrowing on `nameKey` / `flavorKey` /
 * `hintKey` against `apps/web/src/locales/fi.json`.
 *
 * @module data/aarrearkku-catalogue
 */

export type TreasureRarity = 'common' | 'uncommon' | 'rare';

export type IllustrationStatus = 'commissioned' | 'pending';

/**
 * A single treasure slot in the collection.
 *
 * The `KeyT` parameter narrows `nameKey` / `flavorKey` / `hintKey` to a
 * specific `TranslationKey` union when the frontend imports this type.
 * Backends use the default `string` and look up Finnish strings via the
 * server-side i18n instance.
 *
 * State model:
 * - `found === true` → owned; renders with full icon, name, flavor text.
 * - `found === false` → locked tile; opens `ItemHintModal` showing the
 *   universal hint placeholder (per Story 19.7 Phase 3 redirect — per-item
 *   hint copy is deferred to a follow-up story).
 *
 * `illustrationStatus`:
 * - `'commissioned'` → has a working `svgPath` and full name+flavor copy.
 * - `'pending'` → no `svgPath`; first-letter `font-whimsy` fallback in
 *   `ItemTile.tsx` handles rendering for found items, and `ItemHintModal`
 *   handles unfound items.
 */
export interface TreasureItem<KeyT extends string = string> {
  id: string;
  icon?: string;
  svgPath?: string;
  nameKey?: KeyT;
  rarity: TreasureRarity;
  found: boolean;
  /**
   * RESERVED for the pre-prod-release static-fallback follow-up (Story 19.11
   * deferred from v1). When the 30 hand-curated Finnish flavor strings land,
   * `generate-flavor.ts`'s Layer 3 will resolve this key via the server-side
   * i18n instance to write a `flavorSource: 'static'` drop. Until that
   * follow-up ships, the v1 flavor pipeline does not read this field — flavor
   * generation that fails after one transient retry writes
   * `flavorText: null` + `flavorSource: 'placeholder'` and lets S19.13's
   * modal placeholder UX handle the reveal.
   */
  flavorKey?: KeyT;
  hintKey?: KeyT;
  /**
   * Finnish surface forms used by the Story 19.8 word-match drop pipeline
   * to detect this item in generated stories. Lifted from
   * `scripts/extract-items.js` so the runtime matcher and the analysis
   * tool share one source of truth.
   */
  surfaceFormsFi: string[];
  /** Themes most strongly associated with this item; populated in a future story. */
  themesPrimary?: string[];
  /** Secondary themes; populated in a future story. */
  themesSecondary?: string[];
  illustrationStatus: IllustrationStatus;
}

/**
 * Canonical rarity ordering. Grid sorts Common → Uncommon → Rare, then
 * by item `id` ascending as the stable secondary key. The drop selector
 * also walks tiers in this order.
 */
export const RARITY_ORDER: Record<TreasureRarity, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
};

/**
 * The Aarrearkku catalogue (Story 19.7 Phase 4; relocated to shared in S19.11).
 *
 * Distribution: Common 14 / Uncommon 11 / Rare 5 = 30. All 30 items are
 * commissioned (art shipped). `lusikka` + `kompassi`, the S19.20 replacements
 * for the retired `kello`/`nuoli`, received their commissioned art
 * (Treasure_spoon.svg / Treasure_compass.svg) plus hand-authored Finnish
 * name/flavor copy in the S19.20 art follow-up.
 *
 * For the dev mock, the 15 items with full name/flavor copy (the original
 * S19.7 commissioning round minus the retired `kello`, plus the S19.20
 * replacements `lusikka`/`kompassi`) are pre-set as `found: true`; the other
 * 15 are `found: false` so the demo grid renders both locked and unlocked
 * states.
 */
export const MOCK_TREASURES: TreasureItem[] = [
  // ─── Common (14) ───
  {
    id: 'kivi',
    svgPath: '/images/collection/Treasure_stone.svg',
    nameKey: 'collection.treasure.items.stone.name',
    rarity: 'common',
    found: true,
    flavorKey: 'collection.treasure.items.stone.flavor',
    surfaceFormsFi: [
      'kivi',
      'kiven',
      'kiveä',
      'kivellä',
      'kivet',
      'kiviä',
      'kiveen',
      'kivestä',
      'kivensä',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kartta',
    svgPath: '/images/collection/Treasure_map.svg',
    nameKey: 'collection.treasure.items.map.name',
    rarity: 'common',
    found: true,
    flavorKey: 'collection.treasure.items.map.flavor',
    // S19.20: pruned the two verb-twin forms of *karttaa* ("to avoid") —
    // `karttaa` ("avoids") and `kartan` ("I avoid") — which were dropping a
    // map on lines like "hän karttaa riitoja" / "kartan sinua". The nominative
    // `kartta` and the remaining forms aren't verb forms, so real-map mentions
    // still fire. Map is pruned (not retired) because a safe core survives.
    surfaceFormsFi: ['kartta', 'kartalla', 'kartasta', 'karttansa'],
    illustrationStatus: 'commissioned',
  },
  {
    // S19.20: replaces the retired `kello` (bell). "kello" means *clock* far
    // more than bell and fired on every time expression ("kello on viisi" →
    // dropped a bell), a permanent sense-mismatch the word list can't fix.
    // Commissioned art (Treasure_spoon.svg) + hand-authored Finnish copy landed
    // in the S19.20 art follow-up. Forms LOCKED + matcher-verified (8/8 recall;
    // verb `lusikoida` "lapsi lusikoi" + compound `kultalusikka` both safe).
    id: 'lusikka',
    svgPath: '/images/collection/Treasure_spoon.svg',
    nameKey: 'collection.treasure.items.lusikka.name',
    rarity: 'common',
    found: true,
    flavorKey: 'collection.treasure.items.lusikka.flavor',
    surfaceFormsFi: [
      'lusikka',
      'lusikan',
      'lusikkaa',
      'lusikkaan',
      'lusikassa',
      'lusikasta',
      'lusikalla',
      'lusikat',
      'lusikkansa',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'reppu',
    svgPath: '/images/collection/Treasure_backpack.svg',
    nameKey: 'collection.treasure.items.backpack.name',
    rarity: 'common',
    found: true,
    flavorKey: 'collection.treasure.items.backpack.flavor',
    surfaceFormsFi: [
      'reppu',
      'repun',
      'reppua',
      'reppuun',
      'repussa',
      'reppunsa',
      'repusta',
      'repustaan',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'koysi',
    svgPath: '/images/collection/Treasure_rope.svg',
    nameKey: 'collection.treasure.items.rope.name',
    rarity: 'common',
    found: true,
    flavorKey: 'collection.treasure.items.rope.flavor',
    surfaceFormsFi: ['köysi', 'köyden', 'köyttä', 'köydellä', 'köytenä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'peitto',
    svgPath: '/images/collection/Treasure_blanket.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: [
      'peitto',
      'peiton',
      'peittoa',
      'peitolla',
      'peitossa',
      'peittojen',
      'peittonsa',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'laatikko',
    svgPath: '/images/collection/Treasure_box.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: [
      'laatikko',
      'laatikon',
      'laatikkoa',
      'laatikossa',
      'laatikkoon',
      'laatikkonsa',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kirja',
    svgPath: '/images/collection/Treasure_book.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: [
      'kirja',
      'kirjan',
      'kirjaa',
      'kirjassa',
      'kirjasta',
      'kirjat',
      'kirjoja',
      'kirjansa',
      'kirjojen',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'oksa',
    svgPath: '/images/collection/Treasure_branch.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: [
      'oksa',
      'oksan',
      'oksaa',
      'oksalla',
      'oksansa',
      'oksalle',
      'oksalta',
      'oksasta',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'paperi',
    svgPath: '/images/collection/Treasure_paper.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: ['paperi', 'paperin', 'paperia', 'paperille', 'paperit', 'paperinsa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'takki',
    svgPath: '/images/collection/Treasure_coat.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: ['takki', 'takin', 'takkia', 'takkiin', 'takkinsa', 'takkiaan', 'takissa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'tyyny',
    svgPath: '/images/collection/Treasure_pillow.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: ['tyyny', 'tyynyn', 'tyynyä', 'tyynyllä', 'tyynynsä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'lamppu',
    svgPath: '/images/collection/Treasure_lightbulb.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: ['lamppu', 'lampun', 'lamppua', 'lampussa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kengat',
    svgPath: '/images/collection/Treasure_shoes.svg',
    rarity: 'common',
    found: false,
    surfaceFormsFi: ['kengät', 'kenkiä', 'kenkien', 'kengillä', 'kengissään', 'kenkänsä'],
    illustrationStatus: 'commissioned',
  },

  // ─── Uncommon (11) ───
  {
    id: 'kukka',
    svgPath: '/images/collection/Treasure_flower.svg',
    nameKey: 'collection.treasure.items.flower.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.flower.flavor',
    surfaceFormsFi: [
      'kukka',
      'kukan',
      'kukkaa',
      'kukkia',
      'kukat',
      'kukasta',
      'kukalla',
      'kukkansa',
      'kukkien',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'avain',
    svgPath: '/images/collection/Treasure_key.svg',
    nameKey: 'collection.treasure.items.key.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.key.flavor',
    surfaceFormsFi: ['avain', 'avaimen', 'avainta', 'avaimella', 'avaimet', 'avaimia', 'avaimensa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'lyhty',
    svgPath: '/images/collection/Treasure_lantern.svg',
    nameKey: 'collection.treasure.items.lantern.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.lantern.flavor',
    surfaceFormsFi: ['lyhty', 'lyhdyn', 'lyhtyä', 'lyhdyllä', 'lyhdynsä', 'lyhtynsä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'nauha',
    svgPath: '/images/collection/Treasure_ribbon.svg',
    nameKey: 'collection.treasure.items.ribbon.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.ribbon.flavor',
    surfaceFormsFi: ['nauha', 'nauhan', 'nauhaa', 'nauhoja', 'nauhalla', 'nauhansa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'simpukka',
    svgPath: '/images/collection/Treasure_shell.svg',
    rarity: 'uncommon',
    found: false,
    surfaceFormsFi: [
      'simpukka',
      'simpukan',
      'simpukkaa',
      'simpukat',
      'simpukoita',
      'simpukoiden',
      'simpukassa',
      'simpukansa',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    // S19.20: replaces the retired `nuoli` (arrow). `nuoli` is the nominative
    // of the verb *nuolla* "to lick" ("kissa nuoli tassujaan" → dropped an
    // arrow); the collision spans the core forms incl. the nominative, so it's
    // unprunable. Commissioned art (Treasure_compass.svg) + hand-authored
    // Finnish copy landed in the S19.20 art follow-up. Forms LOCKED +
    // matcher-verified (7/7 recall; no verb/name twin; the only figurative
    // hit `moraalinen kompassi` is adult-register, accepted).
    id: 'kompassi',
    svgPath: '/images/collection/Treasure_compass.svg',
    nameKey: 'collection.treasure.items.kompassi.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.kompassi.flavor',
    surfaceFormsFi: [
      'kompassi',
      'kompassin',
      'kompassia',
      'kompassiin',
      'kompassissa',
      'kompassilla',
      'kompassit',
      'kompassinsa',
    ],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kori',
    svgPath: '/images/collection/Treasure_basket.svg',
    rarity: 'uncommon',
    found: false,
    surfaceFormsFi: ['kori', 'korin', 'koriin', 'korissa', 'korinsa', 'korista'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'pallo',
    svgPath: '/images/collection/Treasure_ball.svg',
    rarity: 'uncommon',
    found: false,
    surfaceFormsFi: ['pallo', 'pallon', 'palloa', 'pallolla', 'pallonsa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kyna',
    svgPath: '/images/collection/Treasure_pen.svg',
    nameKey: 'collection.treasure.items.pen.name',
    rarity: 'uncommon',
    found: true,
    flavorKey: 'collection.treasure.items.pen.flavor',
    surfaceFormsFi: ['kynä', 'kynän', 'kynää', 'kynällä', 'kynänsä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'paita',
    svgPath: '/images/collection/Treasure_shirt.svg',
    rarity: 'uncommon',
    found: false,
    surfaceFormsFi: ['paita', 'paidan', 'paitaa', 'paidassa', 'paitansa'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'huivi',
    svgPath: '/images/collection/Treasure_scarf.svg',
    rarity: 'uncommon',
    found: false,
    surfaceFormsFi: ['huivi', 'huivin', 'huivia', 'huivilla', 'huivinsa'],
    illustrationStatus: 'commissioned',
  },

  // ─── Rare (5) ───
  {
    id: 'taskulamppu',
    svgPath: '/images/collection/Treasure_flashlight.svg',
    nameKey: 'collection.treasure.items.flashlight.name',
    rarity: 'rare',
    found: true,
    flavorKey: 'collection.treasure.items.flashlight.flavor',
    surfaceFormsFi: ['taskulamppu', 'taskulampun', 'taskulamppua'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'hoyhen',
    svgPath: '/images/collection/Treasure_feather.svg',
    nameKey: 'collection.treasure.items.feather.name',
    rarity: 'rare',
    found: true,
    flavorKey: 'collection.treasure.items.feather.flavor',
    surfaceFormsFi: ['höyhen', 'höyhenen', 'höyhentä', 'höyheniä', 'höyhenellä', 'höyhenensä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'saapas',
    svgPath: '/images/collection/Treasure_boot.svg',
    nameKey: 'collection.treasure.items.boot.name',
    rarity: 'rare',
    found: true,
    flavorKey: 'collection.treasure.items.boot.flavor',
    surfaceFormsFi: ['saappaat', 'saappaita', 'saappaiden', 'saappaissa', 'saappaissaan'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'kapy',
    svgPath: '/images/collection/Treasure_pinecone.svg',
    nameKey: 'collection.treasure.items.pinecone.name',
    rarity: 'rare',
    found: true,
    flavorKey: 'collection.treasure.items.pinecone.flavor',
    surfaceFormsFi: ['käpy', 'kävyn', 'käpyjä', 'kävyt', 'kävystä'],
    illustrationStatus: 'commissioned',
  },
  {
    id: 'lukko',
    svgPath: '/images/collection/Treasure_lock.svg',
    rarity: 'rare',
    found: false,
    surfaceFormsFi: ['lukko', 'lukon', 'lukkoa', 'lukossa'],
    illustrationStatus: 'commissioned',
  },
];
